import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { InviteMemberButton } from "@components/common/invite-member-button";
import { createMemberOption, createTeamOption } from "@components/common/members-autocomplete/members-autocomplete-utils";
import { EnableGroupCreationCheckbox, GROUP_CREATION_DESCRIPTION } from "@components/groups/enable-group-creation-checkbox";
import { SelectGroupRole } from "@components/role/select-group-role";
import { XgSeatCheckbox } from "@components/table/members/assign-seats/xg-seat-checkbox";
import { useInviteFailuresDialog } from "@components/project/invite-failures-list";
import { Dialog, Text } from "@flat-ui";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useInviteXgSeats } from "@hooks/use-invite-xg-seats";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { Stack } from "@mui-wrapper";
import { useAppParams } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { selectedGroupMembersSelector } from "@store/groups/groups-selector";
import { invalidateWorkspaceMembers } from "@store/members/members-slice";
import { fetchCompanyMembers } from "@store/members/members-slice-thunk";
import { addProcessingGroups, removeProcessingGroups, setOne } from "@store/groups/groups-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { assignableTeamsSelector } from "@store/teams/teams-selector";
import { delay } from "@utils/time-utils";
import { GroupMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { createGroupsPermission } from "@utils/user-utils";
import { useCallback, useMemo, useState } from "react";
import { useTeamUtils } from "@hooks/use-team-utils";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { sortTeams } from "@utils/team-utils";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
import { UtilsTypes } from "@stellar/utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { getInviteHelperText, GROUP_XG_SEAT_DESCRIPTION, getInviteRequestFailures, getInviteResponseFailures, isAnyoneInvited, GROUP_XG_SEAT_REQUIRED_MESSAGE, isMissingXgSeat } from "@utils/invite-xg-seat-utils";
import { doesWorkspaceRoleRequireXgSeat } from "@utils/xg-seat-utils";
/** Renders the invite member button and the functionality in member page */
export function InviteMemberToGroup({
  group,
  buttonVariant
}) {
  const dispatch = useAppDispatch();
  const showFailures = useInviteFailuresDialog();
  const {
    groupId
  } = useAppParams();
  const companyId = useCompanyId();
  const {
    respondToUser
  } = useRespondToInvite();
  const {
    isTeamName
  } = useTeamUtils();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const companyMembers = useCompanyMembers();
  const teams = useAppSelector(assignableTeamsSelector);
  // To tell the members and teams behind the selected values when listing who could not be invited
  const inviteSubjects = {
    members: companyMembers,
    teams
  };
  const groupMembers = useAppSelector(selectedGroupMembersSelector);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldCreateGroups, setShouldCreateGroups] = useState(false);
  const [selectedRole, setSelectedRole] = useState("COMPANY_MANAGER");

  // Workspaces that cannot assign managers hide the role selector and can only invite viewers.
  const roleToAssign = canAssignManagers ? selectedRole : "GROUP_VIEWER";
  const xgSeats = useInviteXgSeats({
    isSeatRequiredForRole: doesWorkspaceRoleRequireXgSeat(roleToAssign),
    seatRequiredMessage: GROUP_XG_SEAT_REQUIRED_MESSAGE,
    location: "invite-member-to-group"
  });
  const {
    memberWithoutSeatDisabledMessage,
    teamDisabledMessage
  } = xgSeats;

  /**
   * Returns why a member or team cannot be invited because they are in the group already, or
   * undefined when they are not
   */
  const getAlreadyInGroupMessage = useCallback(id => {
    const groupMember = groupMembers.find(member => id === member.identity);
    if (!groupMember) {
      return undefined;
    }
    return `Already in group ${groupMember.role ? `as ${UtilsTypes.RoleDisplayNames[groupMember.role]}` : ""}`;
  }, [groupMembers]);

  /** Reformation company members data for faro autocomplete component */
  const memberOptions = useMemo(() => sortMembersByNameOrEmail(companyMembers).map(member_0 => {
    const disabledMessage = getAlreadyInGroupMessage(member_0.identity) ?? (isMissingXgSeat(member_0) ? memberWithoutSeatDisabledMessage : undefined);
    return createMemberOption({
      member: member_0,
      isDisabled: !!disabledMessage,
      disabledMessage: disabledMessage ?? ""
    });
  }), [companyMembers, getAlreadyInGroupMessage, memberWithoutSeatDisabledMessage]);

  /** Reformation teams data for faro autocomplete component */
  const teamOptions = useMemo(() => sortTeams(teams, "name_ascending").map(team => {
    const disabledMessage_0 = getAlreadyInGroupMessage(team.id) ?? teamDisabledMessage;
    return createTeamOption({
      team,
      isDisabled: !!disabledMessage_0,
      disabledMessage: disabledMessage_0 ?? ""
    });
  }), [getAlreadyInGroupMessage, teams, teamDisabledMessage]);
  const autocompleteOptions = useMemo(() => [...teamOptions, ...memberOptions], [teamOptions, memberOptions]);
  const {
    selectedMembers,
    selectedValues,
    uniqueSelectedValues,
    helperText,
    inputHelperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete({
    entityName: "group",
    existingMembers: group.members,
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map(member_1 => member_1.identity)
  });
  const xgSeatsSelection = xgSeats.checkSelection(selectedMembers);

  // Disable the `Send Invite` button if no user has been selected, the input is not a valid email,
  // or somebody selected lacks the Sphere XG seat the role needs.
  const isMembersInputValid = !shouldDisableSendInvite(uniqueSelectedValues, isMemberInputValid);
  const shouldSendInviteDisable = !xgSeatsSelection.isValid || !isMembersInputValid;

  /** Close the dialog and reset loading state */
  async function closeDialog() {
    setIsDialogOpen(false);
    // Delay to ensure dialog closes before resetting loading state
    // This prevents duplicate invitation requests
    await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
    resetSelection();
    setIsLoading(false);
  }

  /** Submit members to be invited */
  async function handleConfirm() {
    setIsLoading(true);

    // The people need their seat before they can be given the role, so those that could not get
    // one are left out, while the rest are invited
    const {
      failures: seatFailures,
      failedValues,
      numberOfSeatsGiven
    } = await xgSeatsSelection.giveSeats();
    // The seats that were given stay assigned whatever fails afterwards
    const areSeatsGiven = numberOfSeatsGiven > 0;
    const identities = uniqueSelectedValues.filter(value => !failedValues.has(value));
    if (!identities.length) {
      showFailures({
        failures: seatFailures,
        isAnyInvited: false,
        areSeatsGiven
      });
      setIsLoading(false);
      return;
    }
    trackEvent({
      name: GroupMemberEvents.inviteMembers,
      props: {
        numberOfMembers: identities.length,
        role: roleToAssign
      }
    });
    try {
      const response = await createPayloadAndSendInvite(identities);
      const responseFailures = response ? getInviteResponseFailures(response, inviteSubjects) : [];
      const failures = [...seatFailures, ...responseFailures];
      if (failures.length) {
        showFailures({
          failures,
          isAnyInvited: isAnyoneInvited(identities, responseFailures, inviteSubjects),
          areSeatsGiven
        });
      } else if (response) {
        respondToUser(response, identities.length);
      }
      void closeDialog();
    } catch (error) {
      setIsLoading(false);
      sentryCaptureError({
        error,
        title: "Cannot invite the members"
      });
      showFailures({
        failures: [...seatFailures, ...getInviteRequestFailures(identities, inviteSubjects, error)],
        isAnyInvited: false,
        areSeatsGiven
      });
    }
  }

  /**
   * Invites the people to the group
   *
   * @param identities The selected values of the people to invite
   * @returns the response, or undefined when there is no group to invite them to
   */
  async function createPayloadAndSendInvite(identities_0) {
    // Additional permission to be added if user should be able to create groups
    const permission = shouldCreateGroups && roleToAssign === "COMPANY_MANAGER" ? createGroupsPermission : undefined;
    const payload = {
      assignments: [{
        identities: identities_0,
        permission,
        role: roleToAssign
      }]
    };
    if (!groupId) {
      return undefined;
    }
    try {
      // Add the groupId to processing groups to start the loading spinner in the group
      dispatch(addProcessingGroups([groupId]));
      const response = await faroCoreApiClient.groups.addMembersToGroup({
        companyId,
        groupId,
        payload
      });

      // Add invited member to the store. response.data.members exist when the response is either success or warning
      if (response.data.members) {
        const groupWithUpdatedMembers = {
          ...group,
          members: response.data.members
        };
        dispatch(setOne(groupWithUpdatedMembers));
        refetchWorkspaceMembers();
      }
      return response;
    } finally {
      // Remove groupId from processing group to stop the loading spinner in the group
      dispatch(removeProcessingGroups([groupId]));
    }
  }

  /**
   * Fetches the workspace members again, since the invite may have added people to the workspace.
   * They were fetched already to list them in the dialog, so the members page would not fetch them
   * again and would miss the new ones, e.g. show that nobody else is in the workspace. A failed
   * fetch is shown as an error toast like every failed fetch of the workspace members, see
   * errorsSlice, and the members page fetches them again once it opens. A fetch that is already
   * on its way can miss the new members, so it is ignored instead of skipping this one.
   */
  function refetchWorkspaceMembers() {
    dispatch(invalidateWorkspaceMembers());
    void dispatch(fetchCompanyMembers({
      companyId
    }));
  }

  /** Default setup when invite dialog open */
  function onOpenInviteDialog() {
    // While the invite is in progress the selection is kept, so it still shows what is invited
    if (!isLoading) {
      resetSelection();
    }
    setIsDialogOpen(true);
  }

  /** Resets the selection along with the choice to give seats */
  function resetSelection() {
    reset();
    xgSeats.reset();
  }

  /**
   * Updates the selected options, unless the invite is in progress: the autocomplete adds the typed
   * text on blur, which also happens when it is disabled
   */
  function onMemberSelect(options) {
    if (!isLoading) {
      handleMemberSelect(options);
    }
  }
  return <>
      <InviteMemberButton buttonVariant={buttonVariant} onClick={onOpenInviteDialog} />

      <Dialog title={canAssignManagers ? "Invite members to group" : "Invite viewers to group"} confirmText="Send Invite" open={isDialogOpen} onConfirm={handleConfirm} showSpinner={isLoading} isConfirmDisabled={shouldSendInviteDisable}
    // Only when the members input is fine too, so that giving the seats lets the invite be sent
    confirmTooltip={isMembersInputValid ? xgSeatsSelection.consentMissingMessage : undefined} onCancel={() => setIsDialogOpen(false)}>
        <Stack>
          <MembersAutocomplete isRequired hasAutoFocus label="Members" helperText={getInviteHelperText({
          inputHelperText,
          selectionHelperText: helperText,
          xgSeatsHelperText: xgSeatsSelection.helperText
        })} options={autocompleteOptions} onInputChange={onInputChange} selectedOptions={selectedMembers} handleMemberSelect={onMemberSelect} isValid={value_0 => isNewEmailValid(value_0) || isTeamName(value_0)} getChipColor={xgSeatsSelection.getChipColor} isDisabled={isLoading} />
        </Stack>
        {canAssignManagers && <Stack sx={{
        marginTop: SPACE_ELEMENTS_OF_MODAL
      }}>
            <SelectGroupRole selectedRole={selectedRole} onChange={setSelectedRole} variant="outlined" isDisabled={isLoading} />
          </Stack>}
        {/* A detail of the role, so it follows the role */}
        <EnableGroupCreationCheckbox selectedRole={roleToAssign} shouldCreateGroups={shouldCreateGroups} membersCount={selectedValues.length} setShouldCreateGroups={setShouldCreateGroups} isDisabled={isLoading} description={GROUP_CREATION_DESCRIPTION} />

        {/* Last, by the button, as the seats are given before the invite is sent */}
        {xgSeatsSelection.giveSeatsCheckbox && <Stack sx={{
        mt: SPACE_ELEMENTS_OF_MODAL,
        pt: SPACE_ELEMENTS_OF_MODAL,
        borderTop: 1,
        borderColor: "divider"
      }}>
            <Text variant="heading14" component="span">
              Sphere XG seat required
            </Text>
            <XgSeatCheckbox {...xgSeatsSelection.giveSeatsCheckbox} isDisabled={isLoading} description={GROUP_XG_SEAT_DESCRIPTION} />
          </Stack>}
      </Dialog>
    </>;
}